import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ShieldCheck, Download, Trash2, Loader2, FileText, Calendar, Globe, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

export default function PrivacyAccount() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const consent = user?.consent || null;

  const downloadExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/users/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `bridal-export-${(user?.email || "user").replace(/[^a-z0-9]/gi, "_")}-${stamp}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Dati scaricati");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setExporting(false); }
  };

  const deleteAccount = async () => {
    if (confirmText !== "ELIMINA") {
      toast.error("Scrivi ELIMINA per confermare");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/users/me", { data: { confirm: "ELIMINA" } });
      toast.success("Account eliminato. I tuoi dati sono stati rimossi.");
      await logout();
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setDeleting(false);
    }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("it-IT") : "—";

  return (
    <div className="max-w-3xl space-y-8" data-testid="privacy-account-page">
      <div>
        <span className="label-uppercase">Account</span>
        <h1 className="font-serif-heading text-4xl sm:text-5xl tracking-tight mt-2">Privacy & dati</h1>
        <p className="mt-3 text-sm text-neutral-600">
          Gestisci i tuoi diritti GDPR: scarica una copia dei tuoi dati, controlla i consensi, elimina l'account.
        </p>
      </div>

      {/* Stato consenso */}
      <section className="bg-white border border-border rounded-md p-6" data-testid="consent-status">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
          </span>
          <div className="flex-1">
            <h2 className="font-serif-heading text-2xl">Stato consenso</h2>
            <p className="text-xs text-neutral-500 mt-0.5">Quando hai accettato Privacy Policy e Termini di Servizio.</p>
          </div>
        </div>

        {consent ? (
          <dl className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-neutral-400" strokeWidth={1.5} />
              <dt className="text-neutral-500 w-28">Data accettazione:</dt>
              <dd className="text-neutral-900 tabular-nums">{fmt(consent.accepted_at)}</dd>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="w-3.5 h-3.5 text-neutral-400" strokeWidth={1.5} />
              <dt className="text-neutral-500 w-28">Versione doc:</dt>
              <dd className="text-neutral-900">{consent.version || "—"}</dd>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-3.5 h-3.5 text-neutral-400" strokeWidth={1.5} />
              <dt className="text-neutral-500 w-28">IP registrato:</dt>
              <dd className="text-neutral-900 font-mono text-xs">{consent.ip || "—"}</dd>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" strokeWidth={1.5} />
              <dt className="text-neutral-500 w-28">Privacy + Termini:</dt>
              <dd className="text-emerald-700 font-medium">Accettati</dd>
            </div>
          </dl>
        ) : (
          <div className="mt-5 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-sm p-3">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.5} />
            <span>Nessun consenso registrato. Il tuo account è precedente all'introduzione del tracciamento consensi (Aprile 2026).</span>
          </div>
        )}
      </section>

      {/* Esporta dati */}
      <section className="bg-white border border-border rounded-md p-6" data-testid="export-section">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Download className="w-4 h-4" strokeWidth={1.5} />
          </span>
          <div className="flex-1">
            <h2 className="font-serif-heading text-2xl">Scarica i tuoi dati</h2>
            <p className="text-sm text-neutral-600 mt-1">
              Esporta in formato <strong>JSON</strong> tutti i dati associati al tuo account: profilo, clienti, eventi, team, preventivi,
              schede, transazioni di pagamento. Conforme all'art. 20 GDPR (portabilità dei dati).
            </p>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={downloadExport} disabled={exporting} className="rounded-full" data-testid="export-button">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} /> : <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />}
            {exporting ? "Preparazione…" : "Scarica copia in JSON"}
          </Button>
        </div>
      </section>

      {/* Elimina account */}
      <section className="bg-white border border-rose-200 rounded-md p-6" data-testid="delete-section">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-full bg-rose-50 text-rose-700 flex items-center justify-center shrink-0">
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </span>
          <div className="flex-1">
            <h2 className="font-serif-heading text-2xl">Elimina account</h2>
            <p className="text-sm text-neutral-600 mt-1">
              Cancellazione <strong>immediata e irreversibile</strong> di tutti i tuoi dati: profilo, clienti, contratti, foto delle prove,
              preventivi, eventi, team. Conforme all'art. 17 GDPR (diritto all'oblio).
            </p>
            <p className="text-xs text-neutral-500 mt-2">
              I dati di pagamento Stripe restano conservati per 10 anni per obblighi fiscali (art. 2220 c.c.).
            </p>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button
            variant="outline"
            className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50"
            onClick={() => { setConfirmText(""); setDeleteOpen(true); }}
            data-testid="delete-open-button"
          >
            <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Elimina il mio account
          </Button>
        </div>
      </section>

      {/* Confirmation dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="rounded-md" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-heading text-2xl flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" strokeWidth={1.5} />
              Conferma eliminazione
            </DialogTitle>
            <DialogDescription>
              L'azione è irreversibile. Tutti i tuoi clienti, contratti, foto e preventivi saranno cancellati definitivamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="label-uppercase">Per confermare scrivi <span className="text-rose-700 font-mono">ELIMINA</span></Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="ELIMINA"
              className="rounded-sm font-mono"
              data-testid="delete-confirm-input"
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setDeleteOpen(false)} disabled={deleting} data-testid="delete-cancel">
              Annulla
            </Button>
            <Button
              className="rounded-sm bg-rose-700 hover:bg-rose-800 text-white"
              disabled={deleting || confirmText !== "ELIMINA"}
              onClick={deleteAccount}
              data-testid="delete-confirm-button"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} /> : <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />}
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
